import { atom, useAtom } from "jotai";
import { toast } from "react-toastify";
import useLeadAutoTaskService from "../../../../services/graal-api/useLeadAutoTaskService";
import useLeadAutoCommentService from "../../../../services/graal-api/useLeadAutoCommentService";
import useLeadAutoStatusService from "../../../../services/graal-api/useLeadAutoStatusService";
import {
  assignUserToLeadAuto,
  onSearchLeadsAuto,
} from "../../../../services/graal-api/leadAutoService";
import useGlobalError from "../../../../hooks/useGlobalError/useGlobalError";
import useStore from "../../../../context/useStore";
import { useContext, useRef } from "react";
import { LeadAutoItemContext } from "./LeadAutoItemContext";
import { formatLeadAutoData } from "../utils/formatLeadAutoData";
import { exporterLeadsAutoJson } from "../utils/exporterLeadsAutoJson";

export const leadAutoItemAtom = atom({});
export const isItemLoadingAtom = atom(false);

const useLeadAutoItem = () => {
  const [state] = useStore();
  const { LEAD_TASKS } = state.constants.items;
  const { leadAutoTaskServiceActions } = useLeadAutoTaskService();
  const { postTask, patchTask, patchTaskStatus, deleteTask } =
    leadAutoTaskServiceActions;
  const { leadAutoCommentServiceActions } = useLeadAutoCommentService();
  const { postComment, patchComment, deleteComment } =
    leadAutoCommentServiceActions;
  const { leadAutoStatusServiceActions } = useLeadAutoStatusService();
  const { patchStatus } = leadAutoStatusServiceActions;
  const [leadAutoItem, setLeadAutoItem] = useAtom(leadAutoItemAtom);
  const [isItemLoading, setIsItemLoading] = useAtom(isItemLoadingAtom);
  const { managers, updateLeadInList } = useContext(LeadAutoItemContext);
  const { globalErrorActions } = useGlobalError();
  const exportJsonEnCours = useRef(false);

  const actions = {
    initItem: (data) => {
      setLeadAutoItem(data);
    },
    refreshLead: async () => {
      try {
        const response = await onSearchLeadsAuto({
          leadUuid: leadAutoItem.id,
        });
        if (response.data.data && response.data.data.length > 0) {
          const freshLead = response.data.data[0];
          const formattedLead = formatLeadAutoData(
            freshLead,
            managers,
            LEAD_TASKS,
          );
          setLeadAutoItem(formattedLead);
          updateLeadInList(leadAutoItem.id, formattedLead);
        }
      } catch (err) {
        console.error("Erreur lors du rafraîchissement du lead:", err);
      }
    },
    onUpdateComments: (comments) => {
      setLeadAutoItem((old) => ({ ...old, comments }));
    },
    onUpdateComment: (comment) => {
      const oldLead = { ...leadAutoItem };
      const updatedComments = [
        ...leadAutoItem.comments.map((c) =>
          c.id === comment.id ? comment : c,
        ),
      ];

      setLeadAutoItem({
        ...leadAutoItem,
        comments: updatedComments,
      });

      updateLeadInList(leadAutoItem.id, { comments: updatedComments });
      setIsItemLoading(true);

      patchComment(leadAutoItem.id, comment.id, {
        contenu: comment.text,
        utilisateursMentionnes: comment.mentionedUsers || [],
      })
        .then((data) => {
          toast.success("Commentaire mis à jour");
          if (data.comments) {
            const formattedComments = actions.mapCommentsFromApi(data.comments);
            setLeadAutoItem({
              ...leadAutoItem,
              comments: formattedComments,
            });
            updateLeadInList(leadAutoItem.id, { comments: formattedComments });
          }
          actions.refreshLead();
        })
        .catch((err) => {
          globalErrorActions.handleGlobalError(err.response?.status, {
            callback: () => actions.onUpdateComment(comment),
          });
          setLeadAutoItem(oldLead);
          updateLeadInList(leadAutoItem.id, { comments: oldLead.comments });
        })
        .finally(() => setIsItemLoading(false));
    },
    onAddComment: (comment) => {
      const oldLead = { ...leadAutoItem };
      const leadId = leadAutoItem.id;
      setIsItemLoading(true);

      postComment(leadId, {
        contenu: comment.text,
        utilisateursMentionnes: comment.mentionedUsers || [],
      })
        .then((response) => {
          toast.success("Commentaire ajouté");
          const currentUser = state.auth.user;
          const userFullName = `${currentUser.firstname} ${currentUser.lastname}`;
          const newComment = {
            id: response.data.id,
            text: comment.text,
            createdBy: userFullName,
            updatedBy: null,
            createdById: currentUser.id,
            updatedById: null,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };

          setLeadAutoItem((currentLead) => {
            const updatedComments = [...currentLead.comments, newComment];
            updateLeadInList(leadId, { comments: updatedComments });
            return {
              ...currentLead,
              comments: updatedComments,
            };
          });
          actions.refreshLead();
        })
        .catch((err) => {
          globalErrorActions.handleGlobalError(err.response?.status, {
            callback: () => actions.onAddComment(comment),
          });
          setLeadAutoItem(oldLead);
          updateLeadInList(leadId, { comments: oldLead.comments });
        })
        .finally(() => setIsItemLoading(false));
    },
    onDeleteComment: (commentId) => {
      const oldLead = { ...leadAutoItem };
      const leadId = leadAutoItem.id;

      setLeadAutoItem((currentLead) => {
        const updatedComments = currentLead.comments.filter(
          (c) => c.id !== commentId,
        );
        updateLeadInList(leadId, { comments: updatedComments });
        return {
          ...currentLead,
          comments: updatedComments,
        };
      });

      setIsItemLoading(true);

      deleteComment(leadId, commentId)
        .then(() => {
          toast.success("Commentaire supprimé");
          actions.refreshLead();
        })
        .catch((err) => {
          globalErrorActions.handleGlobalError(err.response?.status, {
            callback: () => actions.onDeleteComment(commentId),
          });
          setLeadAutoItem(oldLead);
          updateLeadInList(leadId, { comments: oldLead.comments });
        })
        .finally(() => setIsItemLoading(false));
    },
    onUpdateTasks: (tasks) => {
      const oldLead = { ...leadAutoItem };
      setLeadAutoItem({ ...leadAutoItem, tasks });
      setIsItemLoading(true);

      const tasksToUpdate = tasks.map((t, index) => ({
        id: t.id,
        nom: t.taskId,
        commentaire: t.text || null,
        ordre: index + 1,
      }));

      Promise.all(
        tasksToUpdate.map((task) => patchTask(leadAutoItem.id, task.id, task)),
      )
        .then(() => {
          toast.success("Tâches réordonnées");
          const updatedTasks = tasks.map((t, index) => ({
            ...t,
            ordre: index + 1,
          }));
          setLeadAutoItem({
            ...leadAutoItem,
            tasks: updatedTasks,
          });
          updateLeadInList(leadAutoItem.id, { tasks: updatedTasks });
          actions.refreshLead();
        })
        .catch((err) => {
          globalErrorActions.handleGlobalError(err.response?.status, {
            callback: () => actions.onUpdateTasks(tasks),
          });
          setLeadAutoItem(oldLead);
        })
        .finally(() => setIsItemLoading(false));
    },
    onUpdateTask: (task) => {
      const oldLead = { ...leadAutoItem };
      const updatedTasks = [
        ...leadAutoItem.tasks.map((t) => (t.id === task.id ? task : t)),
      ];

      setLeadAutoItem({
        ...leadAutoItem,
        tasks: updatedTasks,
      });

      updateLeadInList(leadAutoItem.id, { tasks: updatedTasks });
      setIsItemLoading(true);

      const isStatusChange =
        oldLead.tasks.find((t) => t.id === task.id)?.isDone !== task.isDone;

      const apiCall = isStatusChange
        ? patchTaskStatus(leadAutoItem.id, task.id, task.isDone)
        : patchTask(leadAutoItem.id, task.id, {
            nom: task.taskId,
            commentaire: task.text || null,
            ordre: task.ordre,
          });

      apiCall
        .then((data) => {
          toast.success("Tâche mise à jour");

          if (data.tasks) {
            const formattedTasks = actions.mapTasksFromApi(data.tasks);
            setLeadAutoItem({
              ...leadAutoItem,
              tasks: formattedTasks,
            });
            updateLeadInList(leadAutoItem.id, { tasks: formattedTasks });
          }

          // Rafraîchir le lead pour récupérer les modifications internes (assignation, etc.)
          actions.refreshLead();
        })
        .catch((err) => {
          globalErrorActions.handleGlobalError(err.response?.status, {
            callback: () => actions.onUpdateTask(task),
          });
          setLeadAutoItem(oldLead);
          updateLeadInList(leadAutoItem.id, { tasks: oldLead.tasks });
        })
        .finally(() => setIsItemLoading(false));
    },
    onAddTask: (task) => {
      const oldLead = { ...leadAutoItem };
      setIsItemLoading(true);

      postTask(leadAutoItem.id, {
        nom: task.taskId,
        commentaire: task.text || null,
        ordre: leadAutoItem.tasks.length + 1,
      })
        .then((response) => {
          toast.success("Tâche ajoutée");
          const taskConstant = LEAD_TASKS.find((t) => t.id === task.taskId);
          const currentUser = state.auth.user;
          const newTask = {
            id: response.data.id,
            _id: response.data.id,
            name: taskConstant?.name || task.taskId,
            text: task.text || "",
            isDone: false,
            taskId: task.taskId,
            ordre: leadAutoItem.tasks.length + 1,
            createdBy: `${currentUser.firstname} ${currentUser.lastname}`,
            updatedBy: "",
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
          };

          setLeadAutoItem((currentLead) => {
            const updatedTasks = [...currentLead.tasks, newTask];
            updateLeadInList(leadAutoItem.id, { tasks: updatedTasks });
            return {
              ...currentLead,
              tasks: updatedTasks,
            };
          });

          // Rafraîchir le lead pour récupérer les modifications internes (assignation, etc.)
          actions.refreshLead();
        })
        .catch((err) => {
          globalErrorActions.handleGlobalError(err.response?.status, {
            callback: () => actions.onAddTask(task),
          });
          setLeadAutoItem(oldLead);
        })
        .finally(() => setIsItemLoading(false));
    },
    onDeleteTask: (taskId) => {
      if (!window.confirm("Confirmez-vous la suppression de cette tâche ?")) {
        return;
      }

      const oldLead = { ...leadAutoItem };
      setIsItemLoading(true);

      deleteTask(leadAutoItem.id, taskId)
        .then(() => {
          const updatedTasks = leadAutoItem.tasks.filter(
            (t) => t.id !== taskId,
          );
          setLeadAutoItem({
            ...leadAutoItem,
            tasks: updatedTasks,
          });
          updateLeadInList(leadAutoItem.id, { tasks: updatedTasks });
          toast.success("Tâche supprimée");
          actions.refreshLead();
        })
        .catch((err) => {
          globalErrorActions.handleGlobalError(err.response?.status, {
            callback: () => actions.onDeleteTask(taskId),
          });
          setLeadAutoItem(oldLead);
        })
        .finally(() => setIsItemLoading(false));
    },
    onUpdateStatus: (newStatus, reason = {}) => {
      const oldLead = { ...leadAutoItem };
      setLeadAutoItem((old) => ({ ...old, status: newStatus, ...reason }));
      updateLeadInList(leadAutoItem.id, { status: newStatus });
      setIsItemLoading(true);

      patchStatus(leadAutoItem.id, newStatus, reason)
        .then((data) => {
          toast.success("Statut mis à jour");

          setLeadAutoItem({
            ...leadAutoItem,
            status: newStatus,
            cancelReason: data.cancelReason || null,
            cancelText: data.cancelText || null,
            assignedTo: data.assignedTo || leadAutoItem.assignedTo,
          });
          updateLeadInList(leadAutoItem.id, {
            status: newStatus,
            cancelReason: data.cancelReason || null,
            cancelText: data.cancelText || null,
            assignedTo: data.assignedTo || leadAutoItem.assignedTo,
          });

          // Rafraîchir le lead pour récupérer les modifications internes (assignation, etc.)
          actions.refreshLead();
        })
        .catch((err) => {
          globalErrorActions.handleGlobalError(err.response?.status, {
            callback: () => actions.onUpdateStatus(newStatus, reason),
          });
          setLeadAutoItem(oldLead);
          updateLeadInList(leadAutoItem.id, { status: oldLead.status });
        })
        .finally(() => setIsItemLoading(false));
    },
    onUpdateAssignTo: (assignedTo, isForce = false) => {
      const oldLead = { ...leadAutoItem };
      setLeadAutoItem((old) => ({ ...old, assignedTo }));
      updateLeadInList(leadAutoItem.id, { assignedTo });
      setIsItemLoading(true);

      const manager = managers.find((m) => m.id === assignedTo);
      assignUserToLeadAuto(
        leadAutoItem.id,
        assignedTo,
        manager?.fullName || null,
        manager?.email || null,
        isForce,
      )
        .then(() => {
          toast.success(`Demande assignée ${isForce ? "(forçage)" : ""}`);

          // Rafraîchir le lead pour récupérer les modifications internes (changement de statut, etc.)
          actions.refreshLead();
        })
        .catch((err) => {
          if (err.response?.data?.assignedToExists) {
            if (
              window.confirm(
                `Cette demande est déjà assignée à \n${err.response.data.assignedToExists}, \nvoulez-vous tout de même la réassigner ?`,
              )
            ) {
              actions.onUpdateAssignTo(assignedTo, true);
            } else {
              setLeadAutoItem(oldLead);
              updateLeadInList(leadAutoItem.id, {
                assignedTo: oldLead.assignedTo,
              });
              setIsItemLoading(false);
            }
          } else {
            globalErrorActions.handleGlobalError(err.response?.status, {
              callback: () => actions.onUpdateAssignTo(assignedTo, isForce),
            });
            setLeadAutoItem(oldLead);
            updateLeadInList(leadAutoItem.id, {
              assignedTo: oldLead.assignedTo,
            });
            setIsItemLoading(false);
          }
        })
        .finally(() => setIsItemLoading(false));
    },
    mapTasksFromApi: (apiTasks) => {
      return apiTasks
        .map((task) => {
          const taskConstant = LEAD_TASKS.find((t) => t.id === task.nom);
          const createdByManager = managers.find((m) => m.id === task.creeePar);
          const updatedByManager = managers.find(
            (m) => m.id === task.modifieePar,
          );
          return {
            id: task.id,
            _id: task.id,
            name: taskConstant?.name || task.nom,
            text: task.commentaire || "",
            isDone: task.statut,
            taskId: task.nom,
            ordre: task.ordre,
            createdBy: createdByManager?.fullName || "",
            updatedBy: updatedByManager?.fullName || "",
            createdAt: task.creeeLe,
            updatedAt: task.modifieeLe,
          };
        })
        .sort((a, b) => a.ordre - b.ordre);
    },
    mapCommentsFromApi: (apiComments) => {
      return apiComments.map((comment) => ({
        id: comment.id,
        text: comment.contenu || "",
        createdBy:
          managers.find((m) => m.id === comment.creePar)?.fullName ||
          comment.creePar,
        updatedBy:
          managers.find((m) => m.id === comment.modifiePar)?.fullName ||
          comment.modifiePar,
        createdAt: comment.creeLe || comment.modifieLe,
        updatedAt: comment.modifieLe,
        createdById: comment.creePar,
        updatedById: comment.modifiePar,
      }));
    },
    scrollItemToTop: () => {
      const leadElem = document.querySelector(
        `#leadautoitem${leadAutoItem.id}`,
      );
      if (leadElem) {
        leadElem.scrollIntoView({ behavior: "smooth" });
      }
    },
    handleExportJSON: async () => {
      if (exportJsonEnCours.current) return;
      exportJsonEnCours.current = true;
      let exportData;
      try {
        const response = await onSearchLeadsAuto({
          leadUuid: leadAutoItem.id,
          avecJournalAgent: true,
        });
        [exportData] = exporterLeadsAutoJson(response.data.data, LEAD_TASKS);
      } catch (err) {
        console.error("Erreur lors de l'export JSON du lead:", err);
        toast.error("Export JSON impossible, réessayez");
        globalErrorActions.handleGlobalError(err.response?.status);
        return;
      } finally {
        exportJsonEnCours.current = false;
      }
      if (!exportData) {
        toast.error("Demande introuvable, export annulé");
        return;
      }
      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const fileName = `lead_auto_${leadAutoItem.id}_${
        new Date().toISOString().split("T")[0]
      }.json`;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
    handleExportText: async () => {
      // Import dynamique de la config et du formatter
      const { questionsConfigAtom, stepsConfig } =
        await import("./formConfigAuto");
      const { generateLeadTextExport } =
        await import("./leadAutoDataFormatter");

      // Récupérer la config des questions
      const questionsConfig = questionsConfigAtom.init;

      // Générer le contenu texte
      const textContent = generateLeadTextExport(
        leadAutoItem,
        questionsConfig,
        stepsConfig,
      );

      // Créer et télécharger le fichier
      const blob = new Blob([textContent], {
        type: "text/plain;charset=utf-8",
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const fileName = `lead_auto_${leadAutoItem.id}_${
        new Date().toISOString().split("T")[0]
      }.txt`;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    },
  };

  return {
    leadAutoItem,
    isItemLoading,
    actions,
  };
};

export default useLeadAutoItem;
